import { useRouter } from "@uirouter/react";
import BN from "bn.js";
import { noop } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import styled from "styled-components";
import { PrimaryButton } from "../../../../components/button";
import { Flex, FullSize } from "../../../../components/layout";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import { GlobalOverlayContext } from "../../../../components/overlay/context";
import { useFetchable } from "../../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../../store/common/fetchable/helpers";
import { Users } from "../../../../store/users/reducer";
import {
  requestChainChange,
  useBrowserWallet,
} from "../../../../store/wallets/crypto";
import { formatEther, getOption } from "../../../../utils/utils";
import { ProfileRoutes } from "../../../profile/routes";
import { WalletsRoutes } from "../../../wallets/routes";
import { StakeContext } from "../context";
import { denominator } from "../form/common";
import StakeModal from "../form/stake-modal";
import Tile from "./tile";

interface IStakeTileProps { }

const Verify = styled(Flex)`
  ${FullSize};
  justify-content: center;
  align-items: flex-end;
  padding: 2em;
  font-weight: 700;
  font-size: 1.3em;
`;

const StyledTile = styled(Tile)`
  border: none;
  background: none;
  box-shadow: none;
`;

const Wrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  font-size: 0.3em;
  align-items: flex-start;
`;

const Amount = styled.div`
  align-self: center;
  font-size: 1.8em;
  margin: 1em 0 0.75em;
`;

const Container = styled.div`
  width: 100%;
`;

const StakeTile: React.FunctionComponent<IStakeTileProps> = (props) => {
  const {
    balance: walletBalance,
    isEthAvailable,
    connect,
  } = useBrowserWallet();
  const { setOverlay } = useContext(GlobalOverlayContext);
  const router = useRouter();
  const user = useFetchable(Users.current);
  const loader = useAppLoader();
  const { isChainSupported, currentContract } = useContext(StakeContext);
  const openStakingModal = useCallback(() => {
    setOverlay({
      children: <StakeModal onClose={() => setOverlay({ children: null })} />,
      title: "Lock $SKRT",
      onClose: () => Promise.resolve(),
    });
  }, [setOverlay, StakeModal]);

  const [isMounted, setIsMounted] = React.useState(false);

  const roomInPoolSize = useMemo(
    () =>
      currentContract
        .map((x) => x.walletInfo)
        .map(({ minStake, poolSize, totalStakedTokens }) =>
          poolSize.gte(minStake.add(totalStakedTokens))
        )
        .getOrElse(false),
    [currentContract]
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (user.status !== "ready" || user.data.kycStatus !== "VERIFIED") {
    return (
      <StyledTile
        title="Your Unlocked $SKRT"
        onClick={
          isFetchableSuccess(user)
            ? () => router.stateService.go(ProfileRoutes.name)
            : noop
        }
        comingSoon={
          <Verify>
            {isFetchableSuccess(user) ? "Please verify yourself" : " "}
          </Verify>
        }
      >
        &nbsp;
      </StyledTile>
    );
  }
  if (!isMounted || !isEthAvailable) {
    return (
      <StyledTile
        title="Your Unlocked $SKRT"
        onClick={connect}
        comingSoon={<Verify>Connect Wallet</Verify>}
      >
        &nbsp;
      </StyledTile>
    );
  }

  if (
    !isChainSupported ||
    !walletBalance.isDefined() ||
    !currentContract.map((x) => x.walletInfo).isDefined()
  )
    return (
      <StyledTile
        onClick={() => loader(requestChainChange("MATIC"))}
        comingSoon={<Verify>Switch to Polygon</Verify>}
      >
        <Container>
          <Wrapper>
            Your $SKRT Balance
            <Amount>$SKRT&nbsp; {formatEther(denominator)}</Amount>
          </Wrapper>
          <Wrapper>
            Your Locked Balance
            <Amount>$SKRT&nbsp; {formatEther(denominator)}</Amount>
          </Wrapper>
        </Container>
      </StyledTile>
    );

  return (
    <StyledTile
      onClick={
        currentContract.map((x) => x.walletInfo).exists((x) => !x.isVerified)
          ? () => router.stateService.go(WalletsRoutes.names.walletList)
          : noop
      }
      comingSoon={
        currentContract
          .map((x) => x.walletInfo)
          .exists((x) => !x.isVerified) ? (
          <Verify>Please verify your wallet!</Verify>
        ) : undefined
      }
      cta={
        <PrimaryButton
          fullWidth
          onClick={openStakingModal}
          disabled={
            currentContract.map(({ active }) => !active).getOrElse(true) ||
            currentContract
              .map((x) => x.walletInfo)
              .exists((x) => !x.isVerified) ||
            !roomInPoolSize
          }
        >
          <strong>{roomInPoolSize ? "LOCK" : "POOL CAPACITY REACHED"}</strong>
        </PrimaryButton>
      }
    >
      <Container>
        <Wrapper>
          Your $SKRT Balance
          <Amount>
            $SKRT&nbsp;{" "}
            {formatEther(
              walletBalance.map((x) => x.amount).getOrElse(new BN(0))
            )}
          </Amount>
        </Wrapper>
        <Wrapper>
          Your Locked Balance
          <Amount>
            $SKRT&nbsp;{" "}
            {formatEther(
              currentContract
                .map((x) => x.walletInfo)
                .map((x) => x.balance)
                .getOrElse(new BN(0))
            )}
          </Amount>
        </Wrapper>
      </Container>
    </StyledTile>
  );
};

export default StakeTile;
